import React, { useContext, useState, useEffect } from 'react';
import Grid from '@mui/material/Grid';
import ServiceOrderList from './ServiceOrderList';
import { CallCardOrders } from './CallCardOrders';
import { PlaceStoreContext } from '../reducers/Place/PlaceContext';
import { WSContext } from '../contexts/WSContext';
import { AuthContext } from '../reducers/Auth/AuthContext';
import AppMessage from './AppMessage';
import Snackbar from './Snackbar';
import { MD5 } from 'object-hash';
import moment from 'moment';
import { TimeAgo } from './TimeAgo';
import LinearProgress from '@mui/material/LinearProgress';

const DelayButton = ({ title, onEnd, mainColor, delay }) => {
  const [linearProgressValue, setLinearProgressValue] = useState(0);
  const [timer, setTimer] = useState(null);
  const [colors, setColors] = useState(mainColor);

  // increment * 100 = delay
  const increment = Math.floor(100 / (delay / 100));

  const handleMouseDown = () => {
    if (timer) {
      clearInterval(timer);
      setTimer(null);
    }

    setColors('txt-white bg-grey');

    const newTimer = setInterval(() => {
      setLinearProgressValue((oldProgress) => {
        if (oldProgress >= 100) {
          onEnd();
          return 0;
          clearInterval(timer);
          setTimer(null);
          setColors(mainColor);
        }

        return oldProgress + increment;
      });
    }, 100);

    setTimer(newTimer);
  };

  const handleMouseUp = () => {
    if (timer) {
      clearInterval(timer);
      setTimer(null);
      setLinearProgressValue(0);
    }
    setColors(mainColor);
  };

  return (
    <div>
      <div className="mr-16" style={{ marginTop: -8 }}>
        {linearProgressValue > 0 && (
          <LinearProgress variant="determinate" value={linearProgressValue} />
        )}
      </div>
      <button
        className={`no-select f3b f10 p-16 ${colors} br-2 mb-16 mr-16 fds-2 mt-8`}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onPointerUp={handleMouseUp}
        onPointerDown={handleMouseDown}
        style={{ minWidth: '160px' }}
      >
        {title}
      </button>
    </div>
  );
};

const ReassignButton = ({ action }) => {
  return (
    <DelayButton title="REASSIGN TO ME" onEnd={action} mainColor="txt-white bg-red" delay={1200} />
  );
};

const UnassingButton = ({ action }) => {
  const handleAction = () => action('EVENTS_UNASSIGN');
  return (
    <DelayButton
      title="UNASSIGN"
      onEnd={handleAction}
      mainColor="txt-white bg-assigned"
      delay={1200}
    />
  );
};

const AssignButton = ({ action }) => {
  const handleAction = () => action('EVENTS_ASSIGN');
  return (
    <DelayButton
      title="ASSIGN TO ME"
      onEnd={handleAction}
      mainColor="txt-white bg-blue"
      delay={1200}
    />
  );
};

const PerPointRender = ({ pointEvents }) => {
  const [serviceDataById, setServiceDataById] = useState({});
  const [callEvents, setCallEvents] = useState([]);
  const [activeEvents, setActiveEvents] = useState(null);
  const [pointHash, setPointHash] = useState({
    point: null,
    hash: null,
  });
  const { WSConnection } = useContext(WSContext);
  const [placeState] = useContext(PlaceStoreContext);
  const {
    services: { serviceId__service },
    products: { productId__product },
  } = placeState;

  const [authState] = useContext(AuthContext);
  const {
    user: { placeId, userId },
  } = authState;

  const [openSnackbar, setOpenSnackbar] = useState(false);

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }

    setOpenSnackbar(false);
  };

  useEffect(() => {
    if (!pointEvents) return;
    const { events } = pointEvents;
    if (!events) return;

    const serviceId__productId__products = {};
    const callId__call = {};

    const newActiveEvents = {};

    for (const event of events) {
      if (event.type === 'call') {
        if (!newActiveEvents[event.userId]) {
          newActiveEvents[event.userId] = {
            calls: {},
            orders: {},
          };
        }

        newActiveEvents[event.userId]['calls'][event.id] = true;
        callId__call[event.id] = event;
        continue;
      }

      if (event.type === 'order') {
        if (!newActiveEvents[event.userId]) {
          newActiveEvents[event.userId] = {
            calls: {},
            orders: {},
          };
        }

        newActiveEvents[event.userId]['orders'][event.orderId] = true;

        const serviceId__productId = {};

        for (const productId in event.orderProducts) {
          const serviceId = productId__product[productId].service_id;
          const newProducts = event.orderProducts[productId];

          if (!serviceId__productId[serviceId]) {
            serviceId__productId[serviceId] = {};
          }

          const hasProductOrder = Array.isArray(serviceId__productId?.[serviceId]?.[productId]);
          if (hasProductOrder) {
            serviceId__productId[serviceId][productId].push(...newProducts);
          } else {
            serviceId__productId[serviceId][productId] = newProducts;
          }
        }

        for (const serviceId in serviceId__productId) {
          if (!serviceId__productId__products[serviceId]) {
            serviceId__productId__products[serviceId] = { ...serviceId__productId[serviceId] };
            continue;
          }

          for (const productId in serviceId__productId[serviceId]) {
            if (serviceId__productId__products[serviceId][productId]) {
              serviceId__productId__products[serviceId][productId].push(
                ...serviceId__productId[serviceId][productId],
              );
            } else {
              serviceId__productId__products[serviceId][productId] =
                serviceId__productId[serviceId][productId];
            }
          }
        }
      }
    }

    setServiceDataById(serviceId__productId__products);
    setCallEvents(Object.values(callId__call));
    setActiveEvents(newActiveEvents);
    const newHash = MD5(serviceId__productId__products);
    const samePoint = pointHash?.point && pointHash.point === pointEvents.pointId;
    if (samePoint && pointHash.hash && newHash !== pointHash.hash) {
      setOpenSnackbar(true);
      return;
    } else {
      setOpenSnackbar(false);
    }
    setPointHash({
      point: pointEvents.pointId,
      hash: newHash,
    });
  }, [pointEvents]);

  const handleAssigning = (type, assignee) => {
    const { pointId } = pointEvents;
    if (!pointId || !userId || !placeId) {
      console.error('NO POINT ID!', placeId, pointId, userId);
    }

    const payload = {
      placeId,
      pointId,
      delivery: details?.mode === 'delivery',
      assigneeUserId: assignee ?? userId,
    };
    const message = {
      type,
      payload,
    };
    const assignEventsMessage = JSON.stringify(message);
    WSConnection.send(assignEventsMessage);
  };

  const handleReassigning = () => {
    const { pointId } = pointEvents;
    if (!pointId || !userId || !placeId) {
      console.error('NO POINT ID!', placeId, pointId, userId);
    }

    const payload = {
      placeId,
      pointId,
      oldAssignee: assignee,
      newAssignee: userId,
    };
    const message = {
      type: 'EVENTS_REASSIGN',
      payload,
    };
    const assignEventsMessage = JSON.stringify(message);
    WSConnection.send(assignEventsMessage);
  };

  const handleConfirm = () => {
    if (!activeEvents) return;
    try {
      if (!pointEvents.pointId) return;
      const message = {
        type: 'CONFIRM_POINT',
        payload: {
          placeId,
          pointId: pointEvents.pointId,
          activeEvents,
          delivery: details?.mode === 'delivery',
        },
      };
      const confirmPointMessage = JSON.stringify(message);
      WSConnection.send(confirmPointMessage);
    } catch (error) {
      console.error('ERROR ON CONFIRM', error);
    }
  };

  const handleReject = () => {
    if (!activeEvents) return;
    try {
      if (!pointEvents.pointId) return;
      const message = {
        type: 'REJECT_POINT',
        payload: { placeId, pointId: pointEvents.pointId, activeEvents },
      };
      const rejectPointMessage = JSON.stringify(message);
      WSConnection.send(rejectPointMessage);
    } catch (error) {
      console.error('ERROR ON CONFIRM', error);
    }
  };

  if (!pointEvents) {
    return <AppMessage message="Select a point event from the list above." />;
  }

  const { requestedServices, assignee, events } = pointEvents;

  const details = events?.[0].details?.delivery?.phone ? events[0].details?.delivery || null : null;

  const assignedToMe = assignee && userId === assignee;
  const assistAvailable = assignee && userId !== assignee;

  const [firstOrder] = events
    .filter((event) => Boolean(event?.orderDate))
    .sort((a, b) => moment(a.orderDate) - moment(b.orderDate));

  return (
    <div className="f-c f-dirc p-32">
      <Snackbar
        severity="error"
        message="New Orders Arrived"
        open={openSnackbar}
        handleClose={handleCloseSnackbar}
      />

      <div className="minH200" />

      {firstOrder?.orderDate && (
        <div className="f-c pt-16 pb-16 f2b">
          <TimeAgo date={firstOrder.orderDate} />
        </div>
      )}

      <Grid container spacing={2} style={{ marginBottom: '16px', marginLeft: '-16px' }}>
        <Grid item xs={12}>
          <div className="f-c f-w">
            {!assignee && <AssignButton action={handleAssigning} />}
            {assignedToMe && <UnassingButton action={handleAssigning} />}
            {assistAvailable && <ReassignButton action={handleReassigning} />}
            <DelayButton
              title="CONFIRM"
              onEnd={handleConfirm}
              mainColor="txt-white bg-success"
              delay={1200}
            />
            <DelayButton
              title="REJECT"
              onEnd={handleReject}
              mainColor="txt-white bg-red"
              delay={1200}
            />
          </div>
        </Grid>
        <Grid item xs={12}></Grid>
      </Grid>
      {callEvents.length > 0 && (
        <Grid container spacing={2} style={{ marginBottom: '16px' }}>
          <div className="f3b f11 f-c W100 b-left p-8">CALLS</div>
          {callEvents.map((callEvent) => {
            return (
              <Grid key={callEvent.id} item xs={4} sm={3} md={2} lg={1}>
                <CallCardOrders event={callEvent} />
              </Grid>
            );
          })}
        </Grid>
      )}
      {details && (
        <div className="f-c f-dirc pt-32 pb-32 mb-32">
          {Object.keys(details).map((key, index) => {
            if (!details[key]) {
              return null;
            }
            return (
              <div className="f-c" key={index}>
                <div className="f12 f2">{key.toUpperCase()}:</div>
                <div className="f12 f2b ml-16">{details[key].toUpperCase()}</div>
              </div>
            );
          })}
        </div>
      )}

      {requestedServices.length > 0 && (
        <Grid container spacing={2}>
          <div className="f3b f11 f-c W100 b-left p-8 mb-8">SERVICES</div>
          {requestedServices.map((serviceId, index) => {
            return (
              <Grid key={index} item xs={12} sm={6} className="b-left b-right">
                <span>{serviceId__service[serviceId]?.name?.toUpperCase()}</span>
                <ServiceOrderList orderProducts={serviceDataById[serviceId]} showTotal />
              </Grid>
            );
          })}
        </Grid>
      )}
    </div>
  );
};

export default PerPointRender;
